import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from 'react-i18next';
import { dataService } from "../../../../Services/data/data.service";
import { makeStyles } from "@material-ui/core/styles";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { CircleOff, DiamondPlus, Factory, List, MapPin, Ellipsis, PencilRuler, ScanBarcode, Camera, Timer, Key, X, Link, TextCursor } from "lucide-react";
import { controlTypes, externalApiTypes, valueTypeCodes } from "../../../../Config/constants";
import Input from "../../../Shared/Input";
import Label from "../../../Shared/Label";
import DatePicker from "../../../Shared/DatePicker";
import Button from "../../../Shared/Buttons/Button";
import { Select, SelectTrigger, SelectContent, SelectItem } from "../../../Shared/Select";

export default function MeasurementEditDialog({ dialogOpen, closeDialog, measurementToEdit, handleDeleteRecord, ...props }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const isStopwatchMeasurement =
    measurementToEdit?.typeId === controlTypes.STOPWATCH ||
    (measurementToEdit?.typeId === controlTypes.EXTERNALAPI && measurementToEdit?.externalApiTypeId === externalApiTypes.STOPWATCH);
  const isCounterMeasurement = measurementToEdit?.typeId === controlTypes.COUNTER;
  const isNumericApiMeasurement =
    measurementToEdit?.typeId === controlTypes.EXTERNALAPI && measurementToEdit?.externalApiTypeId === externalApiTypes.NUMERIC;
  const isNonEditableApiMeasurement =
    measurementToEdit?.typeId === controlTypes.EXTERNALAPI &&
    measurementToEdit?.externalApiTypeId !== externalApiTypes.STOPWATCH &&
    measurementToEdit?.externalApiTypeId !== externalApiTypes.NUMERIC;

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [generalError, setGeneralError] = useState(null);

  const [startTime, setStartTime] = useState(null);
  const [startTimeError, setStartTimeError] = useState(null);

  const [stopTime, setStopTime] = useState(null);
  const [stopTimeError, setStopTimeError] = useState(null);

  const [measurementValue, setMeasurementValue] = useState(null);
  const [measurementValueError, setMeasurementValueError] = useState(null);

  const [commentText, setCommentText] = useState(null);

  const [measurementIsRunning, setMeasurementIsRunning] = useState(false);

  const [dataListOptions, setDataListOptions] = useState([]);
  const isDataListMeasurement = measurementToEdit?.typeId === controlTypes.DATALIST;

  const [priceBreakdown, setPriceBreakdown] = useState([]);

  // Three-dot-menu
  const [openThreeDotMenu, setOpenThreeDotMenu] = useState(false);
  const [threeDotPosition, setThreeDotPosition] = useState({ top: 0, left: 0 }); // Position of the dropdown, relative to the dropdown button
  const openThreeDotMenuButtonRef = useRef(null);
  const threeDotMenuRef = useRef(null);

  useEffect(() => {
    setOpenThreeDotMenu(false);
    if (measurementToEdit) {
      setIsSubmitting(false);
      setMeasurementIsRunning(measurementToEdit?.running ?? false);
      setMeasurementValue(measurementToEdit?.measurementValue ?? null);
      setStartTime(measurementToEdit?.startTime ?? new Date().toISOString());
      setStopTime(measurementToEdit?.stopTime ?? measurementToEdit?.stopValue ?? null);
      setCommentText(measurementToEdit?.commentText ?? null);

      if (measurementToEdit?.typeId === controlTypes.DATALIST && measurementToEdit?.dataListId) {
        dataService.getListItems(measurementToEdit.dataListId).then((result) => {
          if (result) {
            setDataListOptions(result.sort((a, b) => a.name.localeCompare(b.name)));
          }
        });
      } else {
        setDataListOptions([]);
      }

      if (measurementToEdit?.id) {
        dataService.getMeasurementPriceDefinitions(measurementToEdit.id).then((result) => {
          setPriceBreakdown(Array.isArray(result) ? result : []);
        });
      } else {
        setPriceBreakdown([]);
      }
    }
  }, [measurementToEdit]);

  useEffect(() => {
    if (openThreeDotMenu && openThreeDotMenuButtonRef.current) {
      // Calculate the position of the dropdown relative to the viewport
      const rect = openThreeDotMenuButtonRef.current.getBoundingClientRect();
      setThreeDotPosition({
        top: rect.bottom + window.scrollY + 4, // Position dropdown below the trigger
        left: rect.left + window.scrollX, // Align to the right of the trigger
      });
    }
  }, [openThreeDotMenu]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (threeDotMenuRef.current && !threeDotMenuRef.current.contains(event.target) && openThreeDotMenu) {
        // check if target is not the button
        if (!openThreeDotMenuButtonRef.current.contains(event.target)) {
          setOpenThreeDotMenu(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openThreeDotMenu]);

  const handleOpenThreeDotMenu = () => {
    setOpenThreeDotMenu(true);
  };

  const changeMeasurementValue = (e) => {
    setMeasurementValue(e.target.value);
    if (e.target.value.length > 0) {
      setMeasurementValueError(null);
    }
  };

  const handleCloseDialog = () => {
    setIsSubmitting(false);
    resetErrors();
    closeDialog();
  };

  const resetErrors = () => {
    setGeneralError(null);
    setMeasurementValueError(null);
    setStartTimeError(null);
    setStopTimeError(null);
  };

  const changeStartTime = (value) => {
    setStartTime(value);
    if (value) {
      setStartTimeError(null);
      if (stopTime && new Date(stopTime) >= new Date(value)) {
        setStopTimeError(null);
      }
    }
  };

  const changeStopTime = (value) => {
    setStopTime(value);
    if (value) {
      if (startTime && new Date(value) < new Date(startTime)) {
        setStopTimeError(t('measurementEditDialog.stopTimeAfterStart'));
      } else {
        setStopTimeError(null);
      }
    }
  };

  const getMeasurementTypeTag = (type) => {
    switch (type) {
      case controlTypes.COUNTER:
        return (
          <>
            <DiamondPlus size={14} strokeWidth={2} />
            {t('measurementEditDialog.typeCounter')}
          </>
        );
      case controlTypes.STOPWATCH:
        return (
          <>
            <Timer size={14} strokeWidth={2} />
            {t('measurementEditDialog.typeStopwatch')}
          </>
        );
      case controlTypes.FREETEXT:
        return (
          <>
            <TextCursor size={14} strokeWidth={2} />
            {t('measurementEditDialog.typeFreeText')}
          </>
        );
      case controlTypes.DATALIST:
        return (
          <>
            <List size={14} strokeWidth={2} />
            {t('measurementEditDialog.typeDatalist')}
          </>
        );
      case controlTypes.SCANNER:
        return (
          <>
            <ScanBarcode size={14} strokeWidth={2} />
            {t('measurementEditDialog.typeScanner')}
          </>
        );
      case controlTypes.CAMERA:
        return (
          <>
            <Camera size={14} strokeWidth={2} />
            {t('measurementEditDialog.typeCamera')}
          </>
        );
      case controlTypes.EXTERNALAPI:
        return (
          <>
            <Link size={14} strokeWidth={2} />
            {t('measurementEditDialog.typeExternalApi')}
          </>
        );
      default:
        return (
          <>
            <CircleOff size={14} strokeWidth={2} />
            {t('measurementEditDialog.unknown')}
          </>
        );
    }
  };

  const getValueTypeTag = (type) => {
    switch (type) {
      case valueTypeCodes.VA: {
        return (
          <div className={classes.tag} style={{ backgroundColor: "#E2F8E0", border: "none" }}>
            <p style={{ color: "#3AB037" }}>{t('measurementEditDialog.valueAdded')}</p>
          </div>
        );
      }
      case valueTypeCodes.NVA: {
        return (
          <div className={classes.tag} style={{ backgroundColor: "#FEF1D6", border: "none" }}>
            <p style={{ color: "#F69127" }}>{t('measurementEditDialog.noValueAdded')}</p>
          </div>
        );
      }
      case valueTypeCodes.W: {
        return (
          <div className={classes.tag} style={{ backgroundColor: "#FBE5E5", border: "none" }}>
            <p style={{ color: "#D84949" }}>{t('measurementEditDialog.waste')}</p>
          </div>
        );
      }
      default: {
        return (
          <div className={classes.tag} style={{ backgroundColor: "#EDEDED", border: "none" }}>
            <p style={{ color: "#0C0E29" }}>{t('measurementEditDialog.unknown')}</p>
          </div>
        );
      }
    }
  };

  const getInputType = (type, externalApiTypeId) => {
    switch (type) {
      case controlTypes.COUNTER:
        return "number";
      case controlTypes.STOPWATCH:
        return "number";
      case controlTypes.FREETEXT:
        return "text";
      case controlTypes.DATALIST:
        return "text";
      case controlTypes.SCANNER:
        return "text";
      case controlTypes.CAMERA:
        return "text";
      case controlTypes.EXTERNALAPI:
        return externalApiTypeId === externalApiTypes.NUMERIC ? "number" : "text";
      default:
        return "text";
    }
  };

  const handleFinishMeasurement = async (measurementId) => {
    try {
      const response = await dataService.setMeasurementFinished(measurementId);
      if (response?.isSuccess) {
        props.setMeasurementFinished(measurementId);
        setMeasurementIsRunning(false);
      } else if (!response?.isSuccess) {
        setGeneralError(response.message);
      } else {
        setGeneralError(t('measurementEditDialog.errorFinishing'));
      }
    } catch (error) {
      if (error.response?.status === 400) {
        setGeneralError(t('measurementEditDialog.errorInvalidInput'));
      } else if (error.response?.status === 403) {
        setGeneralError(t('measurementEditDialog.errorNoPermissionFinish'));
      } else if (error.response?.status === 500) {
        setGeneralError(t('measurementEditDialog.errorServer'));
      } else {
        setGeneralError(t('measurementEditDialog.errorFinishing'));
      }
    } finally {
      setOpenThreeDotMenu(false); // Close the three-dot menu if it's open
    }
  };

  const validateFields = () => {
    resetErrors();
    let valid = true;

    if (!startTime) {
      setStartTimeError(t('measurementEditDialog.startTimeRequired'));
      valid = false;
    }

    if (isStopwatchMeasurement) {
      if (!stopTime) {
        setStopTimeError(t('measurementEditDialog.stopTimeRequired'));
        valid = false;
      } else if (startTime && new Date(stopTime) < new Date(startTime)) {
        setStopTimeError(t('measurementEditDialog.stopTimeAfterStart'));
        valid = false;
      }
    } else if (!isCounterMeasurement && !isNonEditableApiMeasurement && (measurementValue === null || measurementValue === undefined || measurementValue === "")) {
      setMeasurementValueError(t('measurementEditDialog.valueRequired'));
      valid = false;
    }

    return valid;
  };

  const saveChanges = async () => {
    setIsSubmitting(true);
    const validFields = validateFields();

    if (!validFields) {
      setIsSubmitting(false);
      return;
    }

    const updatedMeasurement = {
      id: measurementToEdit?.id,
      measurementValue: isStopwatchMeasurement ? "" : String(measurementValue ?? ""),
      startTime: startTime,
      stopTime: isStopwatchMeasurement ? stopTime : null,
      commentText: commentText,
      updatedByUserId: props.Login.userid,
    };

    try {
      await dataService.updateMeasurement(updatedMeasurement).then(async (response) => {
        if (response.isSuccess) {
          setMeasurementIsRunning(response.measurement?.running ?? false);
          await props.refreshMeasurements?.();
          handleCloseDialog();
        } else {
          setGeneralError(response.message);
        }
      });
    } catch (error) {
      if (error.response?.status === 400) {
        setGeneralError(t('measurementEditDialog.errorInvalidInput'));
      } else if (error.response?.status === 500) {
        setGeneralError(t('measurementEditDialog.errorServer'));
      } else {
        setGeneralError(t('measurementEditDialog.errorSaving'));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog className={classes.dialog} open={dialogOpen} aria-labelledby="draggable-dialog-title" onClose={handleCloseDialog} classes={{ paper: classes.dialogPaper }}>
      <div className={classes.dialog__head}>
        <DialogTitle title={measurementToEdit?.description ?? t('project.measurement')} className={classes.dialog__title} id="draggable-dialog-title">
          {t('measurementEditDialog.editTitle', { name: measurementToEdit?.description ?? t('project.measurement') })}
        </DialogTitle>
        <div className={classes.head__buttonContainer}>
          <button ref={openThreeDotMenuButtonRef} onClick={() => handleOpenThreeDotMenu()} className={classes.dialog__threeDotButton}>
            <Ellipsis size={19} />
          </button>
          {ReactDOM.createPortal(
            <div
              ref={threeDotMenuRef}
              className={classes.threeDotMenuDropdownContainer}
              style={{ display: openThreeDotMenu ? "block" : "none", top: threeDotPosition.top, left: threeDotPosition.left }}
            >
              {measurementIsRunning && (
                <button className={classes.dropdown__menuItem} onClick={() => handleFinishMeasurement(measurementToEdit?.id)}>
                  {t('measurementEditDialog.markAsFinished')}
                </button>
              )}
              {props.Login.isFlagstoneAdmin && (
                <button className={classes.dropdown__menuItem_negative} onClick={() => handleDeleteRecord(measurementToEdit?.id)}>
                  {t('common.delete')}
                </button>
              )}
            </div>,
            document.body
          )}
          <button onClick={() => handleCloseDialog()} className={classes.dialog__closeButton}>
            <X size={19} />
          </button>
        </div>
      </div>
      <DialogContent className={classes.dialog__contentContainer}>
        <div className={classes.dialog__content}>
          {generalError && (
            <div className={classes.errorContainer} style={{ marginBottom: "16px" }}>
              <p className={classes.errorMessage}>{generalError}</p>
            </div>
          )}
          <div className={classes.measurementLocationDetails}>
            <div className={classes.measurementLocationDetail}>
              <Factory size={14} strokeWidth={2} />
              {measurementToEdit?.companyName ?? t('measurementEditDialog.unknown')}
            </div>
            <div className={classes.measurementLocationDetail}>
              <PencilRuler size={14} strokeWidth={2} />
              {measurementToEdit?.projectName ?? t('measurementEditDialog.unknown')}
            </div>
            <div className={classes.measurementLocationDetail}>
              <MapPin size={14} strokeWidth={2} />
              {measurementToEdit?.resourceName ?? t('measurementEditDialog.unknown')}
            </div>
          </div>
          <div className={classes.tagsContainer}>
            <label className={classes.label}>{t('measurementEditDialog.tags')}</label>

            <div className={classes.tags}>
              {/* Type */}
              <div className={classes.iconTag}>{getMeasurementTypeTag(measurementToEdit?.typeId)}</div>
              {/* Key */}
              {measurementToEdit?.keyValue && (
                <div className={classes.iconTag}>
                  <Key size={14} strokeWidth={2} color={"var(--color-primary)"} />
                  {measurementToEdit?.keyValue}
                </div>
              )}
              {/* Running? */}
              {measurementIsRunning && (
                <div className={classes.iconTag}>
                  <div
                    style={{
                      height: "6px",
                      width: "6px",
                      marginLeft: "2px",
                      backgroundColor: "#F69127",
                      borderRadius: "50%",
                    }}
                  />
                  {t('measurementEditDialog.running')}
                </div>
              )}
              {/* ValueType */}
              {measurementToEdit?.valueTypeId && getValueTypeTag(measurementToEdit?.valueTypeId)}
              {/* EventPrice */}
              {measurementToEdit?.eventPrice > 0 && (
                <div className={classes.tag}>
                  <p>
                    €{measurementToEdit?.eventPrice.toFixed(2)} {measurementToEdit?.eventPriceCurrency}
                  </p>
                </div>
              )}
            </div>
          </div>
          {priceBreakdown && priceBreakdown.length > 0 && (
            <div className={classes.breakdownContainer}>
              <label className={classes.label}>{t('measurementEditDialog.priceBreakdown')}</label>
              <div className={classes.breakdownList}>
                {priceBreakdown.map((item) => {
                  const symbol = item.currencySymbol ?? "";
                  const unitPrice = item.unitPrice != null ? Number(item.unitPrice).toFixed(2) : "—";
                  const total = item.total != null ? Number(item.total).toFixed(2) : "—";
                  return (
                    <div key={item.id} className={classes.breakdownRow}>
                      <div className={classes.breakdownPrimary}>
                        <span className={classes.breakdownCode}>{item.priceDefinitionCode}</span>
                        <span className={classes.breakdownName}>{item.priceDefinitionName}</span>
                      </div>
                      <div className={classes.breakdownMeta}>
                        <span>
                          {Number(item.multiplier).toFixed(2)} × {symbol}
                          {unitPrice}
                        </span>
                        {item.effectiveAccountName && (
                          <span className={classes.breakdownAccount}>{item.effectiveAccountName}</span>
                        )}
                      </div>
                      <strong className={classes.breakdownTotal}>
                        {symbol}
                        {total}
                      </strong>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          <form onSubmit={(e) => e.preventDefault()} className={classes.form}>
            <div style={{ display: "flex", gap: "8px", width: "100%" }}>
              <div className={classes.inputContainer} style={{ width: "50%" }}>
                <Label errorText={startTimeError} required>
                  {isStopwatchMeasurement ? t('measurementEditDialog.startTime') : t('measurementEditDialog.time')}
                </Label>
                <DatePicker value={startTime} onChange={changeStartTime} precision="second" />
              </div>
              {isStopwatchMeasurement && (
                <div className={classes.inputContainer} style={{ width: "50%" }}>
                  <Label errorText={stopTimeError} required>
                    {t('measurementEditDialog.stopTime')}
                  </Label>
                  <DatePicker value={stopTime} onChange={changeStopTime} precision="second" />
                </div>
              )}
              {!isStopwatchMeasurement && (
                <div className={classes.inputContainer} style={{ width: "50%" }}>
                  <Label errorText={measurementValueError} required={!isCounterMeasurement}>
                    {t('common.value')}
                  </Label>
                  {isDataListMeasurement ? (
                    <Select value={measurementValue} onChange={(val) => { setMeasurementValue(val); setMeasurementValueError(null); }} error={!!measurementValueError} placeholder={t('measurementEditDialog.selectValue')} fullwidth>
                      <SelectTrigger>{measurementValue || t('measurementEditDialog.selectValue')}</SelectTrigger>
                      <SelectContent>
                        {dataListOptions.map((item) => (
                          <SelectItem key={item.id} value={item.name}>
                            {item.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input fullwidth value={measurementValue} onChange={changeMeasurementValue} error={measurementValueError} step={isNumericApiMeasurement ? "any" : undefined} type={getInputType(measurementToEdit?.typeId, measurementToEdit?.externalApiTypeId)} readOnly={isCounterMeasurement || isNonEditableApiMeasurement} />
                  )}
                </div>
              )}
            </div>
            {/* TODO: Add comment modification */}
            {/* <div className={classes.inputContainer}>
              <Label errorText={commentTextError}>Comment</Label>
              <Textarea value={commentText} onChange={changeCommentText} />
            </div> */}
          </form>
        </div>
      </DialogContent>
      <DialogActions
        sx={{
          display: "flex",
          justifyContent: "space-between",
          paddingLeft: "16px",
          paddingRight: "16px",
          paddingBottom: "16px",
        }}
      >
        <Button type="button" variant="outlined" onClick={handleCloseDialog}>
          {t('common.cancel')}
        </Button>
        <Button type="button" onClick={saveChanges} loading={isSubmitting}>
          {t('measurementEditDialog.saveChanges')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

const useStyles = makeStyles(() => ({
  dialog: {
    margin: "auto",
  },

  dialogPaper: {
    width: "500px !important",
    height: "auto",
    borderRadius: "6px !important",
  },

  dialog__head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",

    padding: "16px 16px 0px 16px !important",
    marginBottom: "8px !important",
  },

  dialog__title: {
    fontSize: "24px !important",
    fontFamily: "Lato, sans-serif !important",
    fontWeight: "normal",

    maxWidth: "calc(100% - 74px)",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",

    padding: "0px !important",
  },

  head__buttonContainer: {
    display: "flex",
    gap: "8px",
  },

  dialog__closeButton: {
    backgroundColor: "#F5F5F5",
    border: "none",
    cursor: "pointer",

    padding: "3px !important",
    height: "25px !important",

    borderRadius: "3px",

    transition: "all 0.2s",

    "&:hover": {
      backgroundColor: "#D84949",
      color: "#F5F5F5",
    },

    "&:focus-visible": {
      outline: "#4654FB auto 1px",
    },
  },

  dialog__subtitle: {
    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",

    color: "#0C0E29",

    margin: "0px 0px 16px 0px",
  },

  dialog__contentContainer: {
    padding: "0px 16px !important",
    marginBottom: "16px",

    // remove scrollbar
    "&::-webkit-scrollbar": {
      display: "none",
    },
    "-ms-overflow-style": "none",
    scrollbarWidth: "none",
  },

  dialog__content: {
    display: "flex",
    height: "100%",
    flexDirection: "column",
    justifyContent: "flex-start",
    alignItems: "flex-start",

    margin: "0px !important",
  },

  toggleFilter: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",

    position: "relative",

    display: "flex",
    alignItems: "center",

    backgroundColor: "#DCDCDC",
    borderRadius: "4px",

    padding: "1px",
    marginBottom: "16px",
  },

  toggleFilter__optionButton: {
    height: "100%",

    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",

    border: "none",
    borderRadius: "3px",

    color: "#0C0E29",
    backgroundColor: "transparent",

    transition: "all ease-in-out 0.2s",

    zIndex: "1",

    "&:hover": {
      // backgroundColor: "#DCDCDC",
      cursor: "pointer",
    },
  },

  toggleFilter__label: {
    fontFamily: "Lato, sans-serif",
    fontSize: "14px",
    margin: "0",
  },

  toggleSelected: {
    width: "calc(50% - 1px)",
    height: "30px",
    position: "absolute",
    transition: "transform 0.2s",

    backgroundColor: "white",
    borderRadius: "3px",
  },

  errorContainer: {
    width: "100%",
    backgroundColor: "#FDF3F3",

    border: "1px solid #FBE5E5",
    borderRadius: "6px",

    marginBottom: "8px",
    padding: "12px",
  },

  errorMessage: {
    fontSize: "16px",
    color: "#D84949",
  },

  measurementLocationDetails: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "4px",
    flexWrap: "wrap",
    marginBottom: "12px",
  },

  measurementLocationDetail: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    fontSize: "14px",
    color: "#0C0E29",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    padding: "4px 8px 4px 6px",
    borderRadius: "8px",
    backgroundColor: "#EDEDED",
  },

  tagsContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
  },

  breakdownContainer: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    marginBottom: "12px",
  },
  breakdownList: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    border: "1px solid #DCDCDC",
    borderRadius: "8px",
    padding: "8px",
    backgroundColor: "#FFFFFF",
  },
  breakdownRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto auto",
    columnGap: "12px",
    alignItems: "center",
    padding: "4px 6px",
    fontSize: "14px",
    color: "#0C0E29",
    fontFamily: "Lato, sans-serif",
  },
  breakdownPrimary: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  breakdownCode: {
    fontWeight: 600,
  },
  breakdownName: {
    fontSize: "12px",
    color: "#5A5C70",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  breakdownMeta: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    fontSize: "12px",
    color: "#5A5C70",
  },
  breakdownAccount: {
    fontStyle: "italic",
  },
  breakdownTotal: {
    fontVariantNumeric: "tabular-nums",
    minWidth: "72px",
    textAlign: "right",
  },

  label: {
    fontSize: "12px",
    color: "#0C0E29",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    marginBottom: "4px",
  },

  textField: {
    height: "40px",
    width: "100%",

    backgroundColor: "transparent",
    border: "1px solid #DCDCDC",
    borderRadius: "4px",
    outline: "none",

    padding: "0px 12px",

    fontSize: "16px",
    color: "#0C0E29",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",

    "&:focus-visible": {
      outline: "#4654FB auto 1px",
    },

    "&:hover": {
      backgroundColor: "#F5F5F5",
    },
  },

  tags: {
    width: "100%",

    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: "4px",
    flexWrap: "wrap",

    marginBottom: "12px",
  },

  tag: {
    display: "flex",
    alignItems: "center",
    gap: "4px",

    fontSize: "14px",
    color: "#0C0E29",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",

    backgroundColor: "#FFFFFF",
    border: "1px solid #DCDCDC",
    borderRadius: "8px",

    padding: "4px 8px",
  },

  iconTag: {
    display: "flex",
    alignItems: "center",
    gap: "4px",

    fontSize: "14px",
    color: "#0C0E29",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",

    backgroundColor: "#FFFFFF",
    border: "1px solid #DCDCDC",

    padding: "4px 8px 4px 6px",
    borderRadius: "8px",
  },

  form: {
    width: "100%",

    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },

  inputContainer: {
    width: "100%",

    display: "flex",
    flexDirection: "column",
  },

  saveButton: {
    border: "1px solid var(--color-primary) !important",
    background: "var(--color-primary) !important",
    display: "flex",
    color: "white !important",
    justifyContent: "center",
    alignItems: "center",

    "&:hover": {
      background: "rgba(195, 153, 107, 0.85) !important",
    },

    "&:disabled": {
      background: "#bdb6ac !important",
      border: "1px solid grey !important",
      color: "white !important",
      cursor: "not-allowed",
    },
  },

  cancelButton: {
    border: "1px solid var(--color-primary) !important",
    background: "white",
    display: "flex",
    color: "var(--color-primary) !important",
    justifyContent: "center",
    alignItems: "center",

    "&:hover": {
      background: "rgba(195, 153, 107, 0.85) !important",
    },
  },

  primaryButton: {
    height: "40px",
    padding: "0 16px",

    border: "none",
    borderRadius: "4px",

    fontSize: "16px",
    color: "white",
    backgroundColor: "var(--color-primary)",

    transition: "all 0.2s ease-in-out",

    userSelect: "none",

    "&:hover": {
      backgroundColor: "#a77d4f",
      cursor: "pointer",
    },

    "&:disabled": {
      backgroundColor: "#DCDCDC",
      color: "#989898",
      cursor: "not-allowed",
    },
  },

  // Three-dot menu styles
  dialog__threeDotButton: {
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",

    padding: "3px !important",
    height: "25px !important",

    borderRadius: "3px",

    transition: "all 0.2s",

    "&:hover": {
      backgroundColor: "#F5F5F5",
    },

    "&:focus-visible": {
      outline: "#4654FB auto 1px",
    },
  },

  threeDotMenuDropdownContainer: {
    width: "fit-content",
    maxHeight: "200px",
    overflowY: "auto",
    position: "absolute",
    transform: "translate(calc(-100% + 25px), 0)",
    // top: 'calc(100% + 4px)',
    // left: '0',
    zIndex: "1400",
    backgroundColor: "#FFFFFF",
    borderRadius: "4px",
    border: "1px solid #DCDCDC",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",

    display: "flex",
    flexDirection: "column",

    padding: "4px",

    "&::-webkit-scrollbar": {
      width: "4px",
    },

    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "#989898",
      borderRadius: "2px",
    },

    "&::-webkit-scrollbar-track": {
      backgroundColor: "#F4F4F4",
      borderRadius: "2px",
    },

    "&::-webkit-scrollbar-thumb:hover": {
      backgroundColor: "#7C7C7C",
    },
  },

  dropdown__menuItem: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",

    display: "flex",
    alignItems: "center",

    padding: "0px 8px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",

    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#0C0E29",
    backgroundColor: "#FFFFFF",

    "&:hover": {
      backgroundColor: "#F4F4F4",
    },

    "&:active": {
      backgroundColor: "#EDEDED",
    },

    "&:focus": {
      outline: "none",
    },
  },

  dropdown__menuItem_negative: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",

    display: "flex",
    alignItems: "center",

    padding: "0px 8px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",

    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#D84949",
    backgroundColor: "#FFFFFF",

    "&:hover": {
      backgroundColor: "#FDF3F3",
      color: "#C73B3B",
    },

    "&:active": {
      backgroundColor: "#EDEDED",
    },

    "&:focus": {
      outline: "none",
    },
  },

  dropdown__menuItem_positive: {
    width: "100%",
    height: "32px",
    boxSizing: "border-box",

    display: "flex",
    alignItems: "center",

    padding: "0px 8px",
    border: "none",
    borderRadius: "4px",
    cursor: "pointer",

    fontSize: "16px",
    fontFamily: "Lato, sans-serif",
    fontWeight: "normal",
    color: "#3AB037",
    backgroundColor: "#FFFFFF",

    "&:hover": {
      backgroundColor: "#F3FBF2",
      color: "#2B9128",
    },

    "&:active": {
      backgroundColor: "#EDEDED",
    },

    "&:focus": {
      outline: "none",
    },
  },
}));
